import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, RefreshCw, Search, Sparkles, Target, X } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { SkeletonList } from '../components/ui/Skeleton'
import { useTheme } from '../components/providers/ThemeProvider'
import { useBranding } from '../hooks/useBranding'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { GoalService, type Goal, type GoalBoardCard } from '../services/goalService'
import { describeTaskCleanup, lifeAreaLabel } from '../utils/taskForm'
import { groupGoals, matchesQuery, summarize, summaryWords, type GoalGroupBy, type GoalSortBy, type SortDir } from '../utils/goalSections'
import { GoalRow } from '../components/goals/GoalRow'
import { TaskQuickAdd } from '../components/tasks/TaskQuickAdd'

/**
 * The goal list (Goals UX pass 2026-09-06), read the way a person reads it —
 * which goals are moving, which one needs a next step from me, and how far
 * along is each:
 *
 *   - Logo, one title, one summary line, two verbs — the task list's frame.
 *   - A quick-add row: a name is enough; it lands under "Needs a journey".
 *   - Sections by movement: Ready to check · In motion · Needs a journey ·
 *     Waiting for you · Paused · Done (collapsed). Dream / Life area / None
 *     one click away.
 *   - A row is a progress mark, a title, one quiet line: the next thing to do.
 *     Tap the mark to finish the goal (with Undo). The rest under ⋯.
 *   - Search instead of pagination; group, sort and direction remembered.
 */
const GROUP_PREF = 'goals.groupBy'
const SORT_PREF = 'goals.sortBy'
const DIR_PREF = 'goals.sortDir'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

function readPref<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key)
    return (allowed as readonly string[]).includes(v || '') ? (v as T) : fallback
  } catch { return fallback }
}
function writePref(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* per-viewer convenience only */ }
}

export const Goals: React.FC = () => {
  const { isDiary: diaryMode } = useTheme()
  const { assistant: personaName } = useBranding()
  const { timezone } = useUserTimezone()
  const navigate = useNavigate()
  const toast = useToast()
  const [goals, setGoals] = useState<Goal[]>([])
  const [cards, setCards] = useState<Record<string, GoalBoardCard | undefined>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [boardIssue, setBoardIssue] = useState(false)
  const [query, setQuery] = useState('')
  const [groupBy, setGroupByState] = useState<GoalGroupBy>(() => readPref(GROUP_PREF, ['movement', 'dream', 'area', 'none'] as const, 'movement'))
  const [sortBy, setSortByState] = useState<GoalSortBy>(() => readPref(SORT_PREF, ['attention', 'progress', 'created', 'updated'] as const, 'attention'))
  const [sortDir, setSortDirState] = useState<SortDir>(() => readPref(DIR_PREF, ['asc', 'desc'] as const, 'desc'))
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [deleteTarget, setDeleteTarget] = useState<Goal | null>(null)
  const [today, setToday] = useState(todayKey)
  const goalService = useMemo(() => new GoalService(), [])

  useProvideFeedbackContext({ pageType: 'goals_list', extra: { groupBy, sortBy, sortDir, searching: Boolean(query) } })

  const setGroupBy = (g: GoalGroupBy) => { setGroupByState(g); writePref(GROUP_PREF, g) }
  const setSortBy = (s: GoalSortBy) => { setSortByState(s); writePref(SORT_PREF, s) }
  const setSortDir = (d: SortDir) => { setSortDirState(d); writePref(DIR_PREF, d) }

  const load = useCallback(async () => {
    const day = todayKey()
    setToday(day)
    try {
      setError(null)
      const [rows, board] = await Promise.all([
        goalService.getGoals(true),
        goalService.getBoard(day).then((b) => { setBoardIssue(false); return b }).catch(() => { setBoardIssue(true); return null }),
      ])
      setGoals(Array.isArray(rows) ? rows : [])
      setCards(board?.goals || {})
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load goals')
    } finally {
      setLoading(false)
    }
  }, [goalService])

  useEffect(() => { void load() }, [load])

  const visible = useMemo(() => goals.filter((g) => matchesQuery(g, query)), [goals, query])
  const groups = useMemo(() => groupGoals(visible, cards, groupBy, today, lifeAreaLabel, { by: sortBy, dir: sortDir }), [visible, cards, groupBy, today, sortBy, sortDir])
  const summary = useMemo(() => summarize(goals, cards, today), [goals, cards, today])

  // ── verbs ──
  const setStatus = async (goal: Goal, status: 'completed' | 'active', restore?: { status: string; progress: number }) => {
    try {
      await goalService.updateGoal(goal.id, restore ? { status: restore.status, progress: restore.progress } : status === 'completed' ? { status, progress: 100 } : { status })
      await load()
      if (status === 'completed') toast.toast({ variant: 'success', title: 'Done', description: goal.title, action: { label: 'Undo', onClick: () => void setStatus(goal, 'active', { status: goal.status || 'active', progress: goal.progress }) } })
      else toast.success('Back on', goal.title)
    } catch (err) {
      toast.error('Could not record that', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    const target = deleteTarget
    try {
      const result = await goalService.deleteGoal(target.id)
      setDeleteTarget(null)
      await load()
      toast.toast({ variant: 'success', title: 'Goal deleted', description: describeTaskCleanup(result.cleanup) || 'What sat under it stays, on its own.' })
    } catch (err) {
      setDeleteTarget(null)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const quickAdd = async (title: string) => {
    const created = await goalService.createGoal({ title, priority: 'medium' })
    await load()
    toast.toast({ variant: 'success', title: 'Added', description: title, action: created?.id ? { label: 'Plan the journey', onClick: () => navigate(`/goals/${created.id}`) } : undefined })
  }

  const toggle = (key: string, def: boolean) => setCollapsed((c) => ({ ...c, [key]: !(c[key] ?? def) }))

  // ── frame ──
  if (loading) return <div className="space-y-4" aria-busy="true" aria-label="Loading goals"><SkeletonList count={5} /></div>
  if (error && goals.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">The list would not load</h2>
          <p className="mb-4 text-secondary">{error}</p>
          <Button onClick={() => { setLoading(true); void load() }}><RefreshCw className="mr-2 h-4 w-4" />Try again</Button>
        </div>
      </div>
    )
  }

  const empty = goals.length === 0
  const nothingMatches = !empty && visible.length === 0

  return (
    <div className="space-y-4" data-testid="goals-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand sm:h-10 sm:w-10" aria-hidden="true" data-testid="goals-logo">
            <Target className="h-5 w-5 text-on-brand" />
          </span>
          <div className="min-w-0">
            <h1 className={`text-xl font-semibold leading-tight text-primary sm:text-2xl ${diaryMode ? 'font-handwritten' : ''}`}>Goals</h1>
            <p className="text-app-meta text-secondary" data-testid="goals-summary">{summaryWords(summary)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="xs" onClick={() => navigate('/goals/create')} data-testid="goals-new"><Plus className="mr-1 h-4 w-4" />New goal</Button>
          <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=goals&type=goal')} data-testid="goals-ask"><Sparkles className="mr-1 h-4 w-4" />{personaName}</Button>
        </div>
      </div>

      {boardIssue && !empty && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-tint-bad-edge bg-tint-bad-soft p-3 text-bad" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="text-app-meta">We could not load the journeys and what got done, so the rows show the goals only.</span>
          <Button onClick={() => void load()} variant="outline" size="xs" className="ml-auto shrink-0"><RefreshCw className="mr-1.5 h-4 w-4" />Try again</Button>
        </div>
      )}

      {!empty && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="relative block w-full sm:w-64 lg:w-80">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a goal" aria-label="Find a goal"
                   className="min-h-touch w-full rounded-md border border-soft bg-surface pl-8 pr-8 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30 lg:min-h-0 lg:py-1.5" data-testid="goals-search" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-primary"><X className="h-3.5 w-3.5" /></button>}
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-app-micro">
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Group by</span>
              <div role="radiogroup" aria-label="Group by" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['movement', 'Movement'], ['dream', 'Dream'], ['area', 'Life area'], ['none', 'None']] as Array<[GoalGroupBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={groupBy === k} onClick={() => setGroupBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${groupBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`goals-group-${k}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted">Sort</span>
              <div role="radiogroup" aria-label="Sort" className="inline-flex rounded-md border border-soft bg-surface p-0.5">
                {([['attention', 'Attention'], ['progress', 'Progress'], ['created', 'Created'], ['updated', 'Updated']] as Array<[GoalSortBy, string]>).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={sortBy === k} onClick={() => setSortBy(k)} className={`rounded px-2 py-1 font-medium transition-colors ${sortBy === k ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`} data-testid={`goals-sort-${k}`}>{label}</button>
                ))}
              </div>
              {sortBy !== 'attention' && (
                <button type="button" onClick={() => setSortDir(sortDir === 'desc' ? 'asc' : 'desc')} aria-label={sortDir === 'desc' ? 'Highest first. Switch to lowest first' : 'Lowest first. Switch to highest first'}
                        className="inline-flex items-center gap-0.5 rounded-md border border-soft bg-surface px-1.5 py-1 font-medium text-secondary hover:text-primary" data-testid="goals-sort-dir">
                  {sortDir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                  {sortBy === 'progress' ? (sortDir === 'desc' ? 'Furthest along' : 'Just starting') : (sortDir === 'desc' ? 'Newest' : 'Oldest')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <TaskQuickAdd onAdd={quickAdd} onMore={(title) => navigate('/goals/create', { state: title ? { title } : undefined })} autoFocus={empty} placeholder="Add a goal — a name is enough" />

      {empty && (
        <div className="rounded-xl border border-soft bg-surface p-6 text-center" data-testid="goals-empty">
          <p className="text-app-body font-semibold text-primary">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-app-meta text-secondary">
            Put one outcome you want in the box above — a name is enough. The journey there, in tasks, skills and habits, can be planned on the goal itself.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="xs" variant="outline" onClick={() => navigate('/horton-ai?from=goals&type=goal')}><Sparkles className="mr-1 h-4 w-4" />Build with {personaName}</Button>
          </div>
        </div>
      )}

      {nothingMatches && (
        <p className="text-app-meta text-muted" data-testid="goals-no-match">Nothing matches “{query}”. <button type="button" onClick={() => setQuery('')} className="text-brand hover:underline">Clear the search</button></p>
      )}

      {!empty && !nothingMatches && groups.map((g) => {
        const isCollapsed = collapsed[g.key] ?? Boolean(g.collapsedByDefault)
        const Chevron = isCollapsed ? ChevronRight : ChevronDown
        return (
          <section key={g.key} data-testid={`goals-section-${g.key}`} aria-labelledby={`goals-h-${g.key}`}>
            <button type="button" onClick={() => toggle(g.key, Boolean(g.collapsedByDefault))} aria-expanded={!isCollapsed} className="flex w-full items-center gap-1.5 rounded py-1 text-left hover:text-brand" data-testid="goals-section-toggle">
              <Chevron className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden="true" />
              <h2 id={`goals-h-${g.key}`} className="text-app-meta font-bold uppercase tracking-widest text-secondary">{g.title}</h2>
              <span className="text-app-micro text-muted" data-testid="goals-section-count">{g.items.length}</span>
            </button>
            {!isCollapsed && g.items.length > 0 && (
              <div className="mt-1 space-y-1.5">
                {g.items.map((goal) => (
                  <GoalRow key={goal.id} goal={goal} card={cards[String(goal.id)]} showParent={groupBy !== 'dream'} showArea={groupBy === 'dream'} tz={timezone}
                           timeHint={sortBy === 'created' || sortBy === 'updated' ? sortBy : null}
                           onComplete={(gl) => setStatus(gl, 'completed')} onReopen={(gl) => setStatus(gl, 'active')} onDelete={setDeleteTarget} diaryMode={diaryMode} />
                ))}
              </div>
            )}
            {!isCollapsed && g.items.length === 0 && g.quiet && <p className="mt-0.5 pl-5 text-app-meta text-muted" data-testid="goals-section-quiet">{g.quiet}</p>}
          </section>
        )
      })}

      <ConfirmationModal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} title="Delete this goal?"
                         message={`“${deleteTarget?.title}” will be removed, along with the journey steps not yet started. The tasks, habits and skills under it stay, on their own.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default Goals
